import {
    OrganizationConnectionGroupBy,
    OrganizationIntegrationAccountItem,
} from "@common/schemas/organization-connection-groups.schema";
import { View, ViewType } from "@common/types/views/views";
import { financialAccountTypeLabel } from "@common/helpers/financialAccount";
import { GroupByColumnDef } from "@design-system/ViewTable/grouping/buildGroupByViewConfig";
import { ClientCell } from "./Cells/ClientCell";
import { EntityCell } from "./Cells/EntityCell";
import { ConnectedByCell } from "./ConnectedByCell";
import { IntegrationStatusCell } from "./IntegrationStatusCell";
import { IntegrationRowActionsCell } from "./rowActions/IntegrationRowActionsCell";
import { ACTIONS_COLUMN_ID } from "./integrationsViewNormalization";
import { integrationAccountLabel } from "./integrationAccountLabel";

export type IntegrationsGroupByColumn =
    | "account"
    | "type"
    | "login"
    | "client"
    | "entity"
    | "connectedBy"
    | "status"
    | "actions";

export const INTEGRATIONS_GROUP_BY_OPTIONS: Array<{
    key: OrganizationConnectionGroupBy;
    label: string;
    triggerLabel?: string;
}> = [
    { key: "institution", label: "Institution" },
    { key: "type", label: "Type" },
    { key: "login", label: "Login" },
    { key: "client", label: "Client" },
    {
        key: "connectedBy",
        label: "Connected by",
        triggerLabel: "Connected by",
    },
    { key: "status", label: "Status" },
];

type IntegrationsColumnDef = GroupByColumnDef<
    OrganizationIntegrationAccountItem,
    IntegrationsGroupByColumn
>;

export const integrationsGroupByColumns: IntegrationsColumnDef[] = [
    {
        id: "account",
        header: "Account",
        isExpandColumn: true,
        minSize: 280,
        maxSize: 640,
        renderItem: (item) => integrationAccountLabel(item),
    },
    {
        id: "type",
        header: "Type",
        minSize: 120,
        maxSize: 200,
        renderItem: (item) =>
            financialAccountTypeLabel(item.account.type) ?? "-",
    },
    {
        id: "login",
        header: "Login",
        minSize: 180,
        maxSize: 320,
        renderItem: (item) =>
            item.connection.login.institutionName ??
            item.connection.login.email ??
            "-",
    },
    {
        id: "client",
        header: "Client",
        minSize: 200,
        maxSize: 320,
        renderItem: (item) => <ClientCell item={item} />,
    },
    {
        id: "entity",
        header: "Entity",
        minSize: 200,
        maxSize: 320,
        renderItem: (item) => <EntityCell item={item} />,
    },
    {
        id: "connectedBy",
        header: "Connected by",
        minSize: 120,
        maxSize: 200,
        renderItem: (item) => <ConnectedByCell item={item} />,
    },
    {
        id: "status",
        header: "Status",
        minSize: 80,
        maxSize: 120,
        renderItem: (item) => <IntegrationStatusCell item={item} />,
    },
    /**
     * The kebab gutter. `hiddenFromSettings` keeps it out of the column picker
     * entirely, `immutable` stops it being dragged or unpinned, and it opts out
     * of the loading shimmer — a bar where a button will appear reads as content
     * that never arrives. Fixed width: pinned columns size from `minSize`.
     *
     * Absent from `integrationsGroupByView` on purpose: `withActionsColumnLast`
     * injects it on read, which also repairs views cached before it existed.
     */
    {
        id: ACTIONS_COLUMN_ID,
        header: "",
        name: "Actions",
        minSize: 56,
        maxSize: 56,
        immutable: true,
        hiddenFromSettings: true,
        hasSkeleton: false,
        align: "right",
        renderItem: (item) => <IntegrationRowActionsCell item={item} />,
    },
];

export const integrationsGroupByView: View = {
    type: ViewType.ORGANIZATION_INTEGRATIONS,
    columns: [
        { id: "account" },
        { id: "type" },
        { id: "login" },
        { id: "client" },
        { id: "entity" },
        { id: "connectedBy" },
        { id: "status" },
    ],
    filters: {},
    options: {},
};

/**
 * Row selection, turned on by the same flag that gates the bulk action bar —
 * checkboxes on their own would select rows nothing can act on.
 *
 * `bulkActions` is what makes `buildColumns` emit the checkbox column at all;
 * `selection` is what makes those checkboxes visible on row hover, and
 * `alwaysShowSelection` keeps them visible without one — assigning accounts in
 * bulk is the point of this table rather than a shortcut.
 *
 * Merged onto the user's own view rather than replacing it, so turning
 * selection on doesn't discard their column layout.
 */
export const BULK_SELECT_VIEW_OPTIONS = {
    bulkActions: true,
    selection: true,
    alwaysShowSelection: true,
    // Children load a page at a time, so a header "select all" could only ever
    // reach the rows already fetched.
    hideSelectAll: true,
} as const satisfies View["options"];
